import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ShoppingBag } from "lucide-react";
import { Button } from "../../UI/Button";
import {
  notificationApiService,
  parseNotificationOrderId,
  type NotificationItem,
} from "../../../../infrastructure/api/notification-api-service";
import { useNotificationStore } from "../../../stores/notification-store";
import { NOTIFICATION_POLL_INTERVAL_MS } from "../../Common/NotificationBell";

const PAGE_SIZE = 20;

/** "2026.10.23 15:30" */
const formatDateTime = (value: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseOrderNumber = (item: NotificationItem): string | null => {
  if (!item.data) return null;
  try {
    const parsed = JSON.parse(item.data);
    return typeof parsed?.orderNumber === "string" ? parsed.orderNumber : null;
  } catch {
    return null;
  }
};

/**
 * 알림함 내용. 우측 상단 알림 아이콘을 누르면 오는 페이지의 본문이다(레이아웃 없이 그릴 수 있게 페이지와 나눴다).
 * 새 주문 알림을 누르면 읽음 처리하고 해당 주문 상세로 이동한다.
 * 보고 있는 동안에도 헤더 아이콘과 같은 주기로 새 알림을 받아 목록 맨 위에 붙인다.
 */
export const AlarmCenterContent: React.FC<{ companyId: string | number | undefined }> = ({ companyId }) => {
  const navigate = useNavigate();
  const { unreadCount, setUnreadCount, decreaseUnreadCount } = useNotificationStore();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (nextPage: number) => {
    setIsLoading(true);
    setError("");
    try {
      const result = await notificationApiService.getNotifications(nextPage, PAGE_SIZE);
      setItems((previous) => {
        const merged = nextPage === 0 ? result.notifications : [...previous, ...result.notifications];
        return Array.from(new Map(merged.map((item) => [item.id, item])).values());
      });
      setTotalCount(result.totalCount);
      setUnreadCount(result.unreadCount);
      setPage(nextPage);
    } catch {
      setError("알림을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setIsLoading(false);
    }
  }, [setUnreadCount]);

  // 첫 페이지만 다시 받아 새 알림은 앞에 붙이고, 이미 보이는 알림은 읽음 상태만 갱신한다.
  // '더 보기'로 불러온 아래쪽 알림은 그대로 둔다.
  const refreshLatest = useCallback(async () => {
    try {
      const result = await notificationApiService.getNotifications(0, PAGE_SIZE);
      setItems((previous) => {
        const latest = new Map(result.notifications.map((item) => [item.id, item]));
        const known = new Set(previous.map((item) => item.id));
        const fresh = result.notifications.filter((item) => !known.has(item.id));
        return [...fresh, ...previous.map((item) => latest.get(item.id) ?? item)];
      });
      setTotalCount(result.totalCount);
      setUnreadCount(result.unreadCount);
    } catch {
      // 일시적인 실패는 다음 주기에 다시 받는다
    }
  }, [setUnreadCount]);

  useEffect(() => {
    void load(0);
  }, [load]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      void refreshLatest();
    };
    const timer = window.setInterval(tick, NOTIFICATION_POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refreshLatest]);

  const markRead = async (item: NotificationItem) => {
    if (item.isRead) return;
    try {
      await notificationApiService.markAsRead(item.id);
      setItems((previous) => previous.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
      decreaseUnreadCount();
    } catch {
      // 읽음 처리에 실패해도 주문은 열어 준다
    }
  };

  const handleClick = async (item: NotificationItem) => {
    await markRead(item);
    const orderId = parseNotificationOrderId(item);
    if (item.type === "NEW_ORDER" && orderId !== null) {
      const params = new URLSearchParams({ mode: "detail", orderId: String(orderId) });
      const orderNumber = parseOrderNumber(item);
      if (orderNumber) params.set("orderNumber", orderNumber);
      navigate(`/company/${companyId}/orders?${params.toString()}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApiService.markAllAsRead();
      setItems((previous) => previous.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      setError("모두 읽음 처리에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  const hasMore = items.length < totalCount;

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <div className="w-full max-w-[880px] mx-auto px-6 py-8">
        {/* 제목과 버튼을 같은 줄 가운데에 맞춘다 */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="min-w-0 text-left">
            <h2 className="text-[22px] font-bold leading-[30px] text-[#222]">
              알림
              {unreadCount > 0 && (
                <span className="ml-2 align-middle inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#4285F4] text-white text-[12px] font-semibold">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </h2>
            <p className="mt-1 text-[14px] text-[#888]">새 주문 등 확인이 필요한 소식을 모아 봅니다.</p>
          </div>
          <button
            type="button"
            onClick={() => void handleMarkAllRead()}
            disabled={unreadCount === 0}
            className="flex-shrink-0 h-9 px-4 rounded-[8px] border border-[#222] bg-white text-[13px] font-medium text-[#222] hover:bg-[#F5F5F5] disabled:border-[#E0E0E0] disabled:text-[#BBBBBB] disabled:hover:bg-white"
          >
            모두 읽음 처리
          </button>
        </div>

        {error && (
          <p role="alert" className="mb-3 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-2.5 text-[13px] text-[#4285F4] text-left">
            {error}
          </p>
        )}

        <div className="bg-white border border-[#E0E0E0] rounded-[12px] overflow-hidden">
          {items.length === 0 && !isLoading ? (
            <div className="py-16 flex flex-col items-center gap-3 text-[#888]">
              <Bell className="w-8 h-8 text-[#BBBBBB]" strokeWidth={1.5} />
              <span className="text-[14px]">아직 받은 알림이 없습니다.</span>
            </div>
          ) : (
            <ul>
              {items.map((item) => {
                const clickable = item.type === "NEW_ORDER" && parseNotificationOrderId(item) !== null;
                const orderNumber = parseOrderNumber(item);
                return (
                  <li key={item.id} className="border-b border-[#EEEEEE] last:border-b-0">
                    <button
                      type="button"
                      onClick={() => void handleClick(item)}
                      // 아이콘(36px) | 내용 | 시각 세 칸. 아이콘과 시각은 제목 줄(36px 높이)의 가운데에 맞춘다
                      className={`relative w-full grid grid-cols-[36px_1fr_auto] gap-x-3 px-5 py-4 text-left transition-colors ${
                        item.isRead ? "bg-white hover:bg-[#FAFAFA]" : "bg-[#F5F8FF] hover:bg-[#EDF3FF]"
                      } ${clickable || !item.isRead ? "cursor-pointer" : "cursor-default"}`}
                    >
                      {!item.isRead && (
                        <span aria-label="읽지 않음" className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#4285F4]" />
                      )}
                      <span
                        className={`w-9 h-9 rounded-full flex items-center justify-center ${
                          item.isRead ? "bg-[#F5F5F5] text-[#888]" : "bg-[#E8EFFF] text-[#4285F4]"
                        }`}
                      >
                        {item.type === "NEW_ORDER" ? (
                          <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.8} />
                        ) : (
                          <Bell className="w-[18px] h-[18px]" strokeWidth={1.8} />
                        )}
                      </span>
                      <span className="min-w-0 flex flex-col">
                        <span className={`h-9 flex items-center text-[15px] truncate ${item.isRead ? "font-medium text-[#555]" : "font-semibold text-[#222]"}`}>
                          {item.title}
                        </span>
                        <span className="text-[14px] leading-[20px] text-[#555] break-words">{item.message}</span>
                        {orderNumber && (
                          <span className="mt-1 text-[12px] leading-[18px] text-[#888]">주문번호 {orderNumber}</span>
                        )}
                      </span>
                      <span className="h-9 flex items-center text-[12px] text-[#888] whitespace-nowrap">
                        {formatDateTime(item.createdAt)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {hasMore && (
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => void load(page + 1)} disabled={isLoading}>
              {isLoading ? "불러오는 중..." : "더 보기"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
